import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store'
import type {
  PendingEnrollment,
  PendingEnrollmentFilters,
  PendingEnrollmentResult,
  PendingEnrollmentSort,
  PendingEnrollmentState,
} from '@/types/pending-enrollment.types'

const initialFilters: PendingEnrollmentFilters = {
  query: '',
  stage: [],
  priority: [],
  assignedTo: [],
  hasBlockedReason: false,
}

const initialSort: PendingEnrollmentSort = {
  field: 'priority',
  direction: 'desc',
}

const initialState: PendingEnrollmentState = {
  // Search results
  results: null,

  // Current filters
  filters: initialFilters,

  // Sorting
  sort: initialSort,

  // UI state
  isLoading: false,
  isSearching: false,

  // Selected enrollments
  selectedEnrollments: [],

  // View mode
  viewMode: 'table',

  // Error handling
  error: null,

  // Refresh settings
  autoRefresh: true,
  refreshInterval: 30, // 30 seconds
  lastRefreshed: null,
}

const pendingEnrollmentSlice = createSlice({
  name: 'pendingEnrollment',
  initialState,
  reducers: {
    // Search operations
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      state.isSearching = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setSearchResults: (state, action: PayloadAction<PendingEnrollmentResult>) => {
      state.results = action.payload
      state.isLoading = false
      state.isSearching = false
      state.error = null
      state.lastRefreshed = new Date().toISOString()
    },

    setSearchError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
      state.isSearching = false
    },

    clearSearchResults: (state) => {
      state.results = null
      state.error = null
    },

    // Filter management
    updateFilters: (state, action: PayloadAction<Partial<PendingEnrollmentFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },

    resetFilters: (state) => {
      state.filters = initialFilters
      state.sort = initialSort
    },

    setQuickSearch: (state, action: PayloadAction<string>) => {
      state.filters = {
        ...initialFilters,
        query: action.payload,
      }
    },

    // Sorting
    updateSort: (state, action: PayloadAction<PendingEnrollmentSort>) => {
      state.sort = action.payload
    },

    toggleSortDirection: (state, action: PayloadAction<PendingEnrollmentSort['field']>) => {
      if (state.sort.field === action.payload) {
        state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        state.sort = {
          field: action.payload,
          direction: 'asc',
        }
      }
    },

    // Selection management
    setSelectedEnrollments: (state, action: PayloadAction<string[]>) => {
      state.selectedEnrollments = action.payload
    },

    toggleEnrollmentSelection: (state, action: PayloadAction<string>) => {
      const enrollmentId = action.payload
      if (state.selectedEnrollments.includes(enrollmentId)) {
        state.selectedEnrollments = state.selectedEnrollments.filter((id) => id !== enrollmentId)
      } else {
        state.selectedEnrollments.push(enrollmentId)
      }
    },

    selectAllEnrollments: (state) => {
      if (state.results) {
        state.selectedEnrollments = state.results.enrollments.map((e) => e.id)
      }
    },

    clearSelection: (state) => {
      state.selectedEnrollments = []
    },

    // UI state
    setViewMode: (state, action: PayloadAction<PendingEnrollmentState['viewMode']>) => {
      state.viewMode = action.payload
    },

    // Auto refresh settings
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload
    },

    // Update single enrollment in results
    updateEnrollmentInResults: (
      state,
      action: PayloadAction<{
        enrollmentId: string
        updates: Partial<PendingEnrollment>
      }>
    ) => {
      if (state.results) {
        const index = state.results.enrollments.findIndex(
          (e) => e.id === action.payload.enrollmentId
        )
        if (index >= 0) {
          const target = state.results.enrollments[index]
          if (target) {
            Object.assign(target, action.payload.updates)
          }
        }
      }
    },

    // Remove enrollment from results (when completed/approved)
    removeEnrollmentFromResults: (state, action: PayloadAction<string>) => {
      if (state.results) {
        state.results.enrollments = state.results.enrollments.filter((e) => e.id !== action.payload)
        state.results.totalCount -= 1

        // Update selection if the removed enrollment was selected
        state.selectedEnrollments = state.selectedEnrollments.filter((id) => id !== action.payload)
      }
    },

    // Pagination
    updatePage: (state, action: PayloadAction<number>) => {
      if (state.results) {
        state.results.page = action.payload
      }
    },

    // Clear all state
    resetPendingEnrollmentState: (_state) => {
      return initialState
    },
  },
})

// Export actions
export const {
  setSearchLoading,
  setSearchResults,
  setSearchError,
  clearSearchResults,
  updateFilters,
  resetFilters,
  setQuickSearch,
  updateSort,
  toggleSortDirection,
  setSelectedEnrollments,
  toggleEnrollmentSelection,
  selectAllEnrollments,
  clearSelection,
  setViewMode,
  setAutoRefresh,
  setRefreshInterval,
  updateEnrollmentInResults,
  removeEnrollmentFromResults,
  updatePage,
  resetPendingEnrollmentState,
} = pendingEnrollmentSlice.actions

// Selectors
export const selectPendingEnrollment = (state: RootState) => state.pendingEnrollment
export const selectPendingResults = (state: RootState) => state.pendingEnrollment.results
export const selectPendingFilters = (state: RootState) => state.pendingEnrollment.filters
export const selectPendingSort = (state: RootState) => state.pendingEnrollment.sort
export const selectSelectedEnrollments = (state: RootState) =>
  state.pendingEnrollment.selectedEnrollments
export const selectIsSearching = (state: RootState) => state.pendingEnrollment.isSearching
export const selectPendingError = (state: RootState) => state.pendingEnrollment.error
export const selectViewMode = (state: RootState) => state.pendingEnrollment.viewMode
export const selectAutoRefresh = (state: RootState) => state.pendingEnrollment.autoRefresh
export const selectRefreshInterval = (state: RootState) => state.pendingEnrollment.refreshInterval
export const selectLastRefreshed = (state: RootState) => state.pendingEnrollment.lastRefreshed

// Computed selectors
export const selectHasPendingResults = (state: RootState) =>
  state.pendingEnrollment.results && state.pendingEnrollment.results.enrollments.length > 0

export const selectTotalPendingCount = (state: RootState) =>
  state.pendingEnrollment.results?.totalCount || 0

export const selectCurrentPage = (state: RootState) => state.pendingEnrollment.results?.page || 1

export const selectTotalPages = (state: RootState) =>
  state.pendingEnrollment.results?.totalPages || 0

export const selectHasNextPage = (state: RootState) =>
  state.pendingEnrollment.results?.hasNext || false

export const selectHasPreviousPage = (state: RootState) =>
  state.pendingEnrollment.results?.hasPrevious || false

export const selectSelectedCount = (state: RootState) =>
  state.pendingEnrollment.selectedEnrollments.length

export const selectIsAllSelected = (state: RootState) => {
  const results = state.pendingEnrollment.results
  const selected = state.pendingEnrollment.selectedEnrollments

  if (!results || results.enrollments.length === 0) return false
  return selected.length === results.enrollments.length
}

export const selectActiveFiltersCount = (state: RootState) => {
  const filters = state.pendingEnrollment.filters
  let count = 0

  if (filters.query?.trim()) count++
  if (filters.stage && filters.stage.length > 0) count++
  if (filters.priority && filters.priority.length > 0) count++
  if (filters.assignedTo && filters.assignedTo.length > 0) count++
  if (filters.waitingTimeMin !== undefined) count++
  if (filters.waitingTimeMax !== undefined) count++
  if (filters.createdFrom?.trim()) count++
  if (filters.createdTo?.trim()) count++
  if (filters.lastActivityFrom?.trim()) count++
  if (filters.lastActivityTo?.trim()) count++
  if (filters.hasBlockedReason) count++
  if (filters.requiredActions && filters.requiredActions.length > 0) count++

  return count
}

export const selectIsFilterActive = (state: RootState) => selectActiveFiltersCount(state) > 0

export const selectPendingSummary = (state: RootState) => state.pendingEnrollment.results?.summary

// Export reducer
export default pendingEnrollmentSlice.reducer
